import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Checkbox, Dialog, FormControlLabel, makeStyles, RadioGroup } from '@material-ui/core';
import _ from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { CloseIcon, DateIcon } from '../../../../../components/Icon';
import { AutoCompleteSelect } from '../../../../../components';
import StyleButton from '../../../../../components/StyleButton';
import LongRemark from '../../../../../components/LongRemark';
import { i18n } from '../../../../../language';
import { teleVisitAPI } from '../../../../../apis';
import useServiceHoursCheckRules from '../hooks/useServiceHoursCheckRules';
import { KeyboardDatePicker } from '../../../../../components/DatePicker';
import TimeInput from './Field/TimeInput';
import UpdateServiceHours from './UpdateServiceHours';
import RemoveServiceHours from './RemoveServiceHours';
const useStyles = makeStyles(() => ({
  root: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: 'none',
    },
    '& .MuiPaper-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '15px !important',
      boxShadow: '0 -0.125rem 1.3125rem rgba(30, 56, 140, 0.18)',
      margin: '0',
      paddingTop: '2.722rem',
      width: '44.375rem',
    },
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    color: '#2e4469',
  },
  subTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.875rem',
    color: '#7d86a9',
  },
  closeBtn: {
    width: '1.945rem',
    height: '1.945rem',
  },
  closeIcon: {
    fontSize: '1.5rem',
    color: '#538fff',
  },
  input: {
    width: '100%',
    height: '3.5rem',
    borderRadius: '10px',
    border: 'solid 1px #e4e7eb',
    paddingLeft: '1.25rem',
    paddingRight: '1.188rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  inputTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
  },
  date: () => ({
    '& .MuiInputBase-root': {
      borderRadius: '10px',
      backgroundColor: '#ffffff',
      border: 'solid 1px #e4e7eb',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'NotoSans-Bold',
      fontSize: '1rem',
      letterSpacing: '0',
      color: '#2e4469',
      fontWeight: '500',
      width: '100%',
      height: '3.5rem',
      padding: '0',
      paddingLeft: '1rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'unset',
    },
    '& .MuiIconButton-root': {
      padding: '0',
    },
    '& .Mui-disabled': {
      backgroundColor: '#f5f5f5',
    },
  }),
  dateIcon: {
    color: '#c0c6cb',
    fontSize: '1.36rem',
  },
  label: {
    margin: '0',
    marginRight: '0.625rem',
    '& .MuiTypography-body1': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
    },
  },
  radio: {
    color: '#e4e7eb',
  },
  dialogBodyDatePicker: ({ endDayError }) => ({
    '& .MuiInputBase-root': {
      borderRadius: '0.625rem',
      border: endDayError ? '0.0625rem solid #f44336' : '0.0625rem solid #E4E7EB',
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
      fontWeight: 'normal',
      width: '13.5rem',
      height: '3rem',
    },
    '& .MuiButtonBase-root': {
      borderRadius: '0',
      padding: '0',
      '&:hover': {
        backgroundColor: '#ffffff',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      display: 'none',
    },
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: '0.75rem',
    },
    '& .Mui-disabled': {
      backgroundColor: '#f5f5f5',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'NotoSans-Bold',
      fontSize: '1rem',
      letterSpacing: '0',
      color: '#2e4469',
      fontWeight: '500',
      width: '100%',
      padding: '0',
      paddingLeft: '1rem',
    },
  }),
  Picon: {
    fontSize: '1.375rem',
    color: '#538fff',
  },
  checkBox: {
    '&.MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '&.MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '&.Mui-checked': {
      color: '#28d2ab',
    },
  },
  star: {
    fontSize: '0.875rem',
    color: '#ff0000',
  },
  errorMsg: {
    color: '#f44336',
    margin: '3px 0.825rem 0px',
    fontFamily: 'NotoSans-Medium',
    fontWeight: 500,
  },
}));

const initTextByLanguage = ({ language, item }) => (language === 'en-us' ? item.descEN : item.descCH);

const EditServiceHours = ({ onClose, serviceHoursSettingId, date, updateServiceHoursApi, removeServiceHoursApi }) => {
  const [endDayError, setEndDayError] = useState(false);
  const classes = useStyles({ endDayError });
  const loginInfo = useSelector((state) => state.loginInfo);
  const language = useSelector((state) => state.settings.language);
  const [updateServiceHours, setUpdateServiceHours] = useState(false);
  const [removeServiceHours, setRemoveServiceHours] = useState(false);
  const [disabledThisEventOnlyBtn] = useState(false);
  const [, setDetailData] = useState(null);
  const [dropdownData, setDropdownData] = useState(null);
  const [formData, setFormData] = useState({
    serviceHoursColor: '',
    startTime: '',
    endTime: '',
    serviceHoursType: null,
    endDay: null,
    radio: '0',
    durationTime: null,
    bufferTime: null,
    startDay: null,
    date: moment(),
    wardId: loginInfo.wardId,
    type: null,
    hospitalId: loginInfo.hospitalId,
    released: false,
    version: null,
    checkedEndDay: false,
  });
  const { errorMsg, focusCheck, timeCheck } = useServiceHoursCheckRules(formData);
  const handelInputTime = (value) => {
    let inputTime = value;
    if (formData.startTime.length === 1 && inputTime.length === 2) {
      inputTime += ':';
    }
    if (inputTime.length === 3 && inputTime.indexOf(':') === -1) {
      inputTime = `${inputTime.slice(0, 2)}:${inputTime.slice(2, 3)}`;
    }
    if (!/^\\d+$/.test(inputTime)) {
      inputTime = inputTime.replaceAll(/[^0-9:]/g, '');
    }
    if (inputTime.length > 5) {
      inputTime = inputTime.slice(0, 5);
    }
    return inputTime;
  };

  const formHandler = useCallback((key) => {
    switch (key) {
      case 'startDay':
      case 'date':
      case 'serviceHoursColor':
      case 'durationTime':
      case 'bufferTime':
      case 'serviceHoursType':
        return (value) => {
          setFormData((preData) => ({ ...preData, [key]: value }));
        };
      case 'startTime':
      case 'endTime':
        return (value) => {
          setFormData((preData) => ({ ...preData, [key]: handelInputTime(value) }));
        };

      case 'radio':
        return (event) => {
          setFormData((preData) => ({ ...preData, [key]: event.target.value }));
        };
      case 'endDay':
        return (value) => {
          setEndDayError(!value);

          setFormData((preData) => ({ ...preData, [key]: value }));
        };
      default:
        break;
    }
  }, []);
  const checkChange = (name) => {
    return (event) => {
      setFormData({ ...formData, [name]: Number(event.target.checked) });
    };
  };
  const itemLists = useMemo(() => {
    let color = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataKey === 'ServiceHourColor') {
        color.push({
          text: initTextByLanguage({ language, item }),
          value: item.dataValue,
          color: item.dataValue,
        });
      }
    });
    let repeatPattern = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataKey === 'ServiceHourType') {
        repeatPattern.push({
          text: initTextByLanguage({ language, item }),
          value: Number(item.dataValue),
          displaySeq: item.displaySeq,
        });
      }
    });

    repeatPattern = _.sortBy(repeatPattern, ['displaySeq']);

    let durationPerTelevisit = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataKey === 'ServiceHourDuartion') {
        durationPerTelevisit.push({
          text: initTextByLanguage({ language, item }),
          value: Number(item.dataValue),
          displaySeq: item.displaySeq,
        });
      }
    });
    durationPerTelevisit = _.sortBy(durationPerTelevisit, ['displaySeq']);
    
    let bufferTime = [];
    _.map(dropdownData?.staticData, (item, index) => {
      if (item.dataKey === 'ServiceHourBufferTime') {
        bufferTime.push({
          text: initTextByLanguage({ language, item }),
          value: Number(item.dataValue),
        });
      }
    });

    return { color, repeatPattern, durationPerTelevisit, bufferTime };
  }, [dropdownData, language]);

  // get Service Hours Detail
  const getServiceHoursDetailApi = useCallback(async () => {
    try {
      const { result } = await teleVisitAPI.getServiceHoursDetail({
        wardId: loginInfo.wardId,
        serviceHoursSettingId: serviceHoursSettingId,
        date: date,
      });
      if (result.endDay === 0 || result.endDay === null) {
        setFormData({ ...result, endDay: null, checkedEndDay: false });
      } else {
        setFormData({ ...result, checkedEndDay: true });
      }
      setDetailData(result);
    } catch (error) {}
  }, []);
  useEffect(() => {
    (async () => {
      await getServiceHoursDetailApi();
    })();
  }, [getServiceHoursDetailApi]);

  //  update Service Hours
  const updateServiceHoursHandler = useCallback(
    (props) => () => {
      setUpdateServiceHours(props);
    },
    []
  );
  // remove Service Hours
  const removeServiceHoursHandler = useCallback(
    (props) => () => {
      setRemoveServiceHours(props);
    },
    []
  );
  // update Service Hours
  const comfirmHandle = (type) => {
    if (typeof updateServiceHoursApi === 'function') {
      updateServiceHoursApi(
        {
          ...formData,
          type: type,
          hospitalId: loginInfo.hospitalId,
          endDay: formData.checkedEndDay ? formData.endDay : 0,
        },
        onClose
      );
    }
  };
  const removeHandle = (type) => {
    if (typeof removeServiceHoursApi === 'function') {
      removeServiceHoursApi(
        {
          ...formData,
          type: type,
          hospitalId: loginInfo.hospitalId,
          endDay: formData.endDay === null ? 0 : formData.endDay,
        },
        onClose
      );
    }
  };
  // dropdown data
  const staticDataListApi = useCallback(async () => {
    try {
      const { result } = await teleVisitAPI.getStaticDataList();
      setDropdownData(result);
    } catch (error) {}
  }, []);

  useEffect(() => {
    (async () => {
      await staticDataListApi();
    })();
  }, [staticDataListApi]);

  const updateHandle = () => {
    if (formData.checkedEndDay) {
      setEndDayError(!formData.endDay);
      if (!formData.endDay) {
        return;
      }
    }
    formData.serviceHoursType === 0 ? comfirmHandle(0) : setUpdateServiceHours(true);
  };
  const removeBtnHandle = () => {
    formData.serviceHoursType === 0 ? removeHandle(0) : setRemoveServiceHours(true);
  };

  useEffect(() => {
    if (timeCheck(formData.startTime) && timeCheck(formData.endTime)) {
      if (
        moment(formData.endTime, 'HH:mm').diff(moment(formData.startTime, 'HH:mm')) / (1000 * 60) <
        formData.bufferTime + formData.durationTime
      ) {
        formHandler('endTime')('');
      }
    }
  }, [formData.startTime, formData.endTime, formData.bufferTime, formData.durationTime, formHandler]);

  const confirmDisable = useMemo(() => {
    const isError = !errorMsg?.endTime && !errorMsg?.startTime;
    return !(formData.startTime && formData.endTime && isError);
  }, [formData.startTime, formData.endTime, errorMsg]);

  useEffect(() => {
    if (!formData.checkedEndDay) {
      formHandler('endDay')(null);
      setEndDayError(false);
    }
  }, [formData.checkedEndDay, formHandler]);

  const todayFlag = useMemo(
    () => (formData.released ? false : moment().isSame(moment(formData.startDay, 'yyyy-MM-DD'), 'D')),
    [formData.startDay, formData.released]
  );

  return (
    <>
      {!updateServiceHours && !removeServiceHours && (
        <Dialog className={classes.root} open={true}>
          {/* header */}
          <Box display="flex" justifyContent="space-between" mb="1.965rem" p="0 1.875rem">
            <div className={classes.title}>{i18n.t('TELEVISIT_SETTING_POPUP_EDIT_SERVICE_HOUR')}</div>
            <ButtonBase className={classes.closeBtn} onClick={onClose}>
              <CloseIcon className={classes.closeIcon} />
            </ButtonBase>
          </Box>
          {/* body */}
          <Box p="0 1.938rem">
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.25rem" mb="1.25rem">
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <div className={classes.inputTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_START_DAY')} </div>
                <KeyboardDatePicker
                  autoOk
                  // id={id}
                  className={classes.date}
                  size="small"
                  value={formData.date}
                  onChange={formHandler('date')}
                  keyboardIcon={<DateIcon className={classes.dateIcon} />}
                  variant="inline"
                  format="dddd, YYYY-MM-DD"
                  inputVariant="outlined"
                  disabled={true}
                  minDate={formData.date}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <div className={classes.inputTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_COLOR')} </div>
                <AutoCompleteSelect
                  type="color"
                  itemList={itemLists.color}
                  value={formData.serviceHoursColor}
                  onChange={formHandler('serviceHoursColor')}
                  disabled={Boolean(formData.released)}
                />
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.25rem" mb="1.875rem">
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <div className={classes.inputTitle}>
                  {i18n.t('TELEVISIT_SETTING_POPUP_START_TIME')}
                  <span className={classes.star}>*</span>{' '}
                </div>
                <TimeInput
                  onBlur={focusCheck('startTime', todayFlag)}
                  placeholder={i18n.t('TELEVISIT_SETTING_POPUP_START_TIME')}
                  value={formData.startTime}
                  onChange={formHandler('startTime')}
                  errorMsg={errorMsg?.startTime}
                  disabled={Boolean(formData.released)}
                  staticData={dropdownData?.staticData}
                  today={todayFlag}
                  inputProps={{ value: formData.startTime }}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <Box className={classes.inputTitle} display="flex">
                  {i18n.t('TELEVISIT_SETTING_POPUP_END_TIME')}
                  <span className={classes.star}>*</span>
                  <LongRemark
                    content={
                      <div dangerouslySetInnerHTML={{ __html: i18n.t('TELEVISIT_SETTING_POPUP_END_TIME_REMARK') }} />
                    }
                    width="100%"
                    minWidth="10rem"
                    id="longremark-zoom"
                  />
                </Box>
                <TimeInput
                  onBlur={focusCheck('endTime', todayFlag)}
                  errorMsg={errorMsg?.endTime}
                  placeholder={i18n.t('TELEVISIT_SETTING_POPUP_END_TIME')}
                  value={formData.endTime}
                  onChange={formHandler('endTime')}
                  disabled={Boolean(formData.released)}
                  staticData={dropdownData?.staticData}
                  today={todayFlag}
                  inputProps={{ value: formData.endTime }}
                />
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.25rem" mt="1.25rem" mb="1.875rem">
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <Box className={classes.inputTitle} display="flex">
                  {i18n.t('TELEVISIT_SETTING_POPUP_DURATION')}
                  <LongRemark
                    content={i18n.t('TELEVISIT_SETTING_POPUP_DURATION_REMARK')}
                    width="100%"
                    minWidth="10rem"
                    id="longremark-zoom"
                  />
                </Box>
                <AutoCompleteSelect
                  itemList={itemLists.durationPerTelevisit}
                  value={formData.durationTime}
                  onChange={formHandler('durationTime')}
                  disabled={Boolean(formData.released)}
                />
              </Box>
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <Box className={classes.inputTitle} display="flex">
                  {i18n.t('TELEVISIT_SETTING_POPUP_BUFFER_TIME')}
                  <LongRemark
                    content={i18n.t('TELEVISIT_SETTING_POPUP_BUFFER_TIME_REMARK')}
                    width="100%"
                    minWidth="10rem"
                    id="longremark-zoom"
                  />
                </Box>
                <AutoCompleteSelect
                  itemList={itemLists.bufferTime}
                  value={formData.bufferTime}
                  onChange={formHandler('bufferTime')}
                  disabled={Boolean(formData.released)}
                />
              </Box>
            </Box>

            <div className={classes.subTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_CUSTOM_RECURRENCE')}</div>
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.25rem" mt="1.25rem">
              <Box display="flex" flexDirection="column" gridRowGap="0.625rem" flexBasis="50%">
                <div className={classes.inputTitle}>{i18n.t('TELEVISIT_SETTING_POPUP_REPEAT_PATTERN')} </div>
                <AutoCompleteSelect
                  itemList={itemLists.repeatPattern}
                  value={formData.serviceHoursType}
                  onChange={formHandler('serviceHoursType')}
                  disabled={true}
                />
              </Box>
              <Box display="flex" flexBasis="50%" />
            </Box>
            {formData.serviceHoursType !== 0 && (
              <Box display="flex" mt="1.25rem">
                <RadioGroup row onChange={formHandler('radio')}>
                  <FormControlLabel
                    className={classes.label}
                    value="1"
                    control={
                      <Checkbox
                        className={classes.checkBox}
                        id="DutyAssign-RadioGroup-afb-1"
                        disabled={Boolean(formData.released)}
                      />
                    }
                    checked={Boolean(formData.checkedEndDay)}
                    label={i18n.t('TELEVISIT_SETTING_POPUP_END_DAY')}
                    style={{ marginBottom: '1.75rem' }}
                    labelPlacement="end"
                    onChange={checkChange('checkedEndDay')}
                    // checked={formData.endDay ? true : false}
                  />
                  {/* endDay Can only be changed to be greater than the day that the click opens */}
                  <Box display="flex" flexDirection="column">
                    <KeyboardDatePicker
                      autoOk
                      className={classes.dialogBodyDatePicker}
                      keyboardIcon={
                        <DateIcon
                          className={classes.Picon}
                          style={{ color: formData.endDay === null || !formData.released ? '#c0c6cb' : '#538fff' }}
                        />
                      }
                      value={formData.endDay}
                      onChange={formHandler('endDay')}
                      id="DutyAssign-KeyboardDatePicker-afd"
                      variant="inline"
                      format="dddd, YYYY-MM-DD"
                      inputVariant="outlined"
                      InputAdornmentProps={{ position: 'end' }}
                      placeholder="Select end date"
                      disabled={Boolean(formData.released) || !formData.checkedEndDay}
                      minDate={moment(formData.date)}
                      minDateMessage="start day after end day."
                      // disabled={formData.radio !== '1'}
                      // minDate={moment(time, 'D/M/YYYY').add(1, 'd').format('YYYY-MM-DD')}
                      // maxDate={moment(time, 'D/M/YYYY').add(365, 'd').format('YYYY-MM-DD')}
                      // onBlur={focusCheck('weeklyEndTime', formData.weeklyEndTime)}
                    />
                    {endDayError && (
                      <div className={classes.errorMsg}>{i18n.t('SETTING_TELEVISIT_SERVICE_HOUR_END_DAY_ERROR')}</div>
                    )}
                  </Box>
                </RadioGroup>
              </Box>
            )}
          </Box>
          {/* footer */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            p="1.125rem"
            mt="1.5rem"
            borderTop="solid 1px #e4e7eb"
            gridColumnGap="1.25rem"
          >
            <StyleButton
              text={i18n.t('TELEVISIT_SETTING_POPUP_REMOVE')}
              width="12.5rem"
              height="2.875rem"
              type="fill"
              backgroundColor="#ff655c"
              onClick={removeBtnHandle}
              disabled={Boolean(formData.released)}
            />
            <StyleButton
              text={i18n.t('TELEVISIT_SETTING_POPUP_UPDATE')}
              width="12.5rem"
              height="2.875rem"
              type="fill"
              backgroundColor="#28d2ab"
              onClick={updateHandle}
              disabled={formData.released || confirmDisable}
            />
          </Box>
        </Dialog>
      )}
      {updateServiceHours && (
        <UpdateServiceHours
          onClose={updateServiceHoursHandler(false)}
          open={updateServiceHours}
          comfirmHandle={comfirmHandle}
          disabledThisEventOnlyBtn={disabledThisEventOnlyBtn}
        />
      )}
      {removeServiceHours && (
        <RemoveServiceHours
          onClose={removeServiceHoursHandler(false)}
          removeHandle={removeHandle}
          disabledThisEventOnlyBtn={disabledThisEventOnlyBtn}
        />
      )}
    </>
  );
};

export default EditServiceHours;
